import type { ConsoleBrandDetail } from '@rsecure/brand-contracts/console';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { ErrorState, Loading, Ltr, Notice, Time } from '../../components/ui';
import { api } from '../../lib/api';

const STAGE_LABEL: Record<string, string> = {
  candidate_import: 'Candidate import',
  source_lookup: 'Source lookup',
  provider_fetch: 'Provider fetch',
  snapshot_storage: 'Snapshot storage',
  identity_verification: 'Identity verification',
  asset_processing: 'Asset processing',
  normalization: 'Normalization',
  review: 'Review',
  approval: 'Approval',
  publication: 'Publication',
  refresh: 'Refresh',
};

export function TimelineTab({ d }: { d: ConsoleBrandDetail }) {
  const q = useQuery({
    queryKey: ['timeline', d.entity.slug],
    queryFn: () => api.timeline(d.entity.slug),
  });
  if (q.isError) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  if (!q.data) return <Loading />;
  const missing = q.data.coverage.filter((c) => c.events === 0).map((c) => STAGE_LABEL[c.stage]);
  return (
    <div className="grid" style={{ gap: 16 }}>
      <section className="card">
        <div className="card-h">
          <h2>Stage evidence</h2>
          <span className="muted" style={{ fontSize: 12 }}>
            every event below is derived from a persisted record
          </span>
        </div>
        <div className="card-b">
          <div className="coverage" data-testid="stage-coverage">
            {q.data.coverage.map((c) => (
              <span
                key={c.stage}
                className={`cv ${c.events ? '' : 'none'}`}
                title={c.events ? `${c.events} event(s)` : 'No persisted evidence'}
              >
                {STAGE_LABEL[c.stage]} <b>{c.events || '—'}</b>
              </span>
            ))}
          </div>
          {missing.length > 0 && (
            <p className="muted" style={{ fontSize: 12.5, margin: '10px 0 0' }}>
              No persisted evidence for: {missing.join(', ')}.
              {d.snapshots.some((s) => s.requestKind.startsWith('legacy')) &&
                ' This brand came from the historical import: its provider data was restored from the archive, so there was no live source lookup or fetch.'}
            </p>
          )}
        </div>
      </section>
      {q.data.events.length === 0 ? (
        <Notice tone="warn">No persisted events for this brand.</Notice>
      ) : (
        <section className="card">
          <div className="card-b">
            <ol className="tl" data-testid="timeline">
              {q.data.events.map((e, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: events may share a source record
                <li key={`${e.source}-${i}`}>
                  <span className={`node ${e.outcome}`} aria-hidden="true" />
                  <div>
                    <div className="head">
                      <span className="badge">{STAGE_LABEL[e.stage]}</span>
                      <span className="title">{e.title}</span>
                      <span className="when">
                        <Time iso={e.at} />
                      </span>
                    </div>
                    {e.detail && <div className="detail">{e.detail}</div>}
                    <div className="source">
                      {e.source.startsWith('brand_ingestion_runs:') ? (
                        <Link className="link" to={`/operations/runs/${e.source.split(':')[1]}`}>
                          <Ltr mono>{e.source}</Ltr>
                        </Link>
                      ) : (
                        <Ltr mono>{e.source}</Ltr>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>
      )}
    </div>
  );
}
